import React from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {PlanInfo, FOUNDER, PlanName} from 'obvio/Billing/plans'
import {usePlan} from 'obvio/Billing/PlanProvider'
import {BillingInterval} from 'obvio/Billing/plans'
import {useGetSubscription} from 'obvio/Billing/subscribe'
import {obvioRoutes} from 'obvio/Routes'
import {colors} from 'lib/ui/theme'
import {useBillingTransaction} from 'obvio/Billing/BillingTransactionProvider'

export default function PlanActionButton(props: {
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {interval, plan} = props
  const {
    canCancel,
    canDowngrade,
    canResume,
    canUpgrade,
    isDowngradingTo,
  } = usePlan()
  const subscription = useGetSubscription()
  const {isTransactionPending} = useBillingTransaction()

  if (isDowngradingTo(plan, subscription)) {
    return null
  }

  if (canResume(plan, subscription)) {
    return (
      <ResumePlanButton
        disabled={isTransactionPending}
        interval={interval}
        plan={plan}
      />
    )
  }

  if (canCancel(plan, subscription)) {
    return (
      <CancelPlanButton
        disabled={isTransactionPending}
        interval={interval}
        plan={plan}
      />
    )
  }

  if (canUpgrade(plan, subscription)) {
    return (
      <UpgradePlanButton
        disabled={isTransactionPending}
        interval={interval}
        plan={plan}
      />
    )
  }

  if (canDowngrade(plan, subscription)) {
    return (
      <DowngradePlanButton
        disabled={isTransactionPending}
        interval={interval}
        plan={plan}
      />
    )
  }

  return (
    <ChoosePlanButton
      disabled={isTransactionPending}
      interval={interval}
      plan={plan}
    />
  )
}

const StyledLink = styled((props) => {
  const {children, ...otherProps} = props

  return (
    <RelativeLink disableStyles {...otherProps}>
      {children}
    </RelativeLink>
  )
})<{
  children: React.ReactElement | React.ReactElement[]
}>`
  width: 100%;
`

const planUrl = (
  route: string,
  planName: PlanName,
  interval: BillingInterval,
): string => {
  let url = ''

  switch (route) {
    case 'cancel':
      url = `${obvioRoutes.billing.cancel_plan}?plan=${planName}`
      break
    case 'resume':
      url = `${obvioRoutes.billing.resume_plan}?plan=${planName}`
      break
    case 'downgrade':
      url = `${obvioRoutes.billing.change_plan}?plan=${planName}&downgrade`
      break
    case 'upgrade':
    case 'choose':
      url = `${obvioRoutes.billing.change_plan}?plan=${planName}`
      break
  }

  return `${url}&interval=${interval}`
}

function CancelPlanButton(props: {
  disabled?: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {disabled, interval, plan} = props
  const cancelUrl = planUrl('cancel', plan.name, interval)

  return (
    <StyledLink disabled={disabled} to={cancelUrl}>
      <StyledButton
        color={colors.error}
        aria-label={`cancel ${plan.name} plan`}
      >
        Cancel Plan
      </StyledButton>
    </StyledLink>
  )
}

function ResumePlanButton(props: {
  disabled?: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {disabled, interval, plan} = props
  const resumeUrl = planUrl('resume', plan.name, interval)

  return (
    <StyledLink disabled={disabled} to={resumeUrl}>
      <StyledButton
        color={colors.primary}
        aria-label={`resume ${plan.name} plan`}
      >
        Resume Plan
      </StyledButton>
    </StyledLink>
  )
}

function UpgradePlanButton(props: {
  disabled?: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {disabled, interval, plan} = props
  const upgradeUrl = planUrl('upgrade', plan.name, interval)

  if (plan.name === FOUNDER) {
    return (
      <ChoosePlanButton disabled={disabled} interval={interval} plan={plan} />
    )
  }

  return (
    <StyledLink disabled={disabled} to={upgradeUrl}>
      <StyledButton aria-label={`upgrade ${plan.name} plan`}>
        Upgrade Plan
      </StyledButton>
    </StyledLink>
  )
}

function ChoosePlanButton(props: {
  disabled?: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {disabled, interval, plan} = props
  const upgradeUrl = planUrl('choose', plan.name, interval)

  return (
    <StyledLink disabled={disabled} to={upgradeUrl}>
      <StyledButton aria-label={`choose ${plan.name} plan`}>
        Choose Plan
      </StyledButton>
    </StyledLink>
  )
}

function DowngradePlanButton(props: {
  disabled?: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {disabled, interval, plan} = props
  const downgradeUrl = planUrl('downgrade', plan.name, interval)

  return (
    <StyledLink disabled={disabled} to={downgradeUrl}>
      <StyledButton aria-label={`downgrade ${plan.name} plan`}>
        Downgrade Plan
      </StyledButton>
    </StyledLink>
  )
}

const StyledButton = styled((props) => {
  const {children, color: _, ...otherProps} = props

  return (
    <Button variant="outlined" fullWidth {...otherProps}>
      {children}
    </Button>
  )
})<{
  color: string
}>`
  background-color: transparent !important;
  border: solid 1px ${(props) => props.color};
  color: ${(props) => props.color};
  margintop: 9px;
`
